import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { api } from "../../lib/api";

// Shared by the one-way / combined Create Booking page and the separate-PNR
// round-trip view (CreateRoundTripFromTbo.jsx).

export function useDebounce(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export const userLabel = (u) =>
  `${[u.first_name, u.last_name].filter(Boolean).join(" ") || "Unnamed"} (${u.email || u.mobile_no || `#${u.user_id}`})`;

export function UserDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api
      .get("/admin/users", { params: { page: 1, limit: 20, search: debouncedQuery || undefined } })
      .then((res) => !cancelled && setUsers(res.data?.data || []))
      .catch((e) => toast.error(e?.response?.data?.message || e.message || "Failed to load users"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, debouncedQuery]);

  useEffect(() => {
    const onClick = (e) => boxRef.current && !boxRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="mt-2 w-full border rounded-xl px-3 py-2 text-left outline-none focus:ring-2 focus:ring-black flex items-center justify-between gap-2"
      >
        <span className={`truncate ${value ? "" : "text-gray-400"}`}>{value ? userLabel(value) : "Select a user"}</span>
        <span className="text-gray-500 text-xs">▼</span>
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border rounded-xl shadow-lg overflow-hidden">
          <input
            autoFocus
            className="w-full border-b px-3 py-2 text-sm outline-none"
            placeholder="Search by name, email or mobile"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
            ) : users.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No users found.</div>
            ) : (
              users.map((u) => (
                <button
                  type="button"
                  key={u.user_id}
                  onClick={() => {
                    onChange(u);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    value?.user_id === u.user_id ? "bg-gray-100 font-semibold" : ""
                  }`}
                >
                  {userLabel(u)}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function JsonPanel({ title, data }) {
  const [open, setOpen] = useState(false);
  const text = data == null ? "(empty)" : JSON.stringify(data, null, 2);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${title} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="border rounded-2xl overflow-hidden flex flex-col min-w-0">
      <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex items-center justify-between gap-2">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{open ? "▼" : "▶"}</span>
          {title}
        </button>
        <button type="button" onClick={copy} className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-white">
          Copy
        </button>
      </div>
      {open && (
        <pre className="text-xs text-gray-800 p-4 overflow-auto max-h-[70vh] whitespace-pre-wrap break-all font-mono">{text}</pre>
      )}
    </div>
  );
}

export const show = (v) => (v === null || v === undefined || v === "" ? "-" : typeof v === "object" ? JSON.stringify(v) : String(v));
export const fmtDateTime = (v) => (v ? String(v).replace("T", " ").slice(0, 16) : "-");

export function FieldGrid({ fields }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 text-sm">
      {fields.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <div className="text-gray-500">{label}</div>
          <div className="font-semibold break-all">{show(value)}</div>
        </div>
      ))}
    </div>
  );
}

export function Section({ title, children }) {
  return (
    <div className="border rounded-2xl overflow-hidden">
      <div className="bg-gray-50 px-4 py-3 text-sm font-semibold">{title}</div>
      <div className="p-4 overflow-x-auto">{children}</div>
    </div>
  );
}

export function SimpleTable({ columns, rows }) {
  if (!rows?.length) return <div className="text-sm text-gray-500">None</div>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-gray-600">
          {columns.map(([label]) => (
            <th key={label} className="px-2 py-2 whitespace-nowrap">{label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t">
            {columns.map(([label, get]) => (
              <td key={label} className="px-2 py-2 whitespace-nowrap">{show(get(r))}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Seat / meal a passenger has on one segment, matched by route. TBO's
// "NoSeat" / "NoMeal" placeholders count as nothing; a Meal without a route
// (full-service airlines) applies to every segment.
const segmentRoute = (s) => [
  s.origin?.airport?.airport_code ?? s.origin?.airport_code,
  s.destination?.airport?.airport_code ?? s.destination?.airport_code,
];
const onSegment = (item, seg) => {
  const [from, to] = segmentRoute(seg);
  return !item?.origin || !item?.destination || (item.origin === from && item.destination === to);
};
const isRealAddon = (item) => item && !/^no(seat|meal)$/i.test(String(item.code ?? "").trim());
const seatLabel = (x) => x.code || [x.row_no, x.seat_no].filter(Boolean).join("") || null;
const mealLabel = (x) => x.airline_description || (x.description && x.description !== "undefined" ? x.description : null) || x.code || null;

// One entry per passenger; the name is added only when there are several.
function segmentAddons(passengers, seg, field, label) {
  const pax = passengers || [];
  const parts = pax
    .map((p) => {
      const values = (Array.isArray(p[field]) ? p[field] : [])
        .filter((x) => isRealAddon(x) && onSegment(x, seg))
        .map(label)
        .filter(Boolean);
      if (!values.length) return null;
      return pax.length > 1 ? `${p.first_name || "Pax"}: ${values.join(", ")}` : values.join(", ");
    })
    .filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

/** The bookings / fare_details / passengers / segments rows a create will insert. */
export function BookingRowsView({ rows, paymentIdOverride }) {
  const b = rows?.booking;
  const fd = rows?.fare_details;
  if (!b) return null;
  return (
    <>
      <Section title="Booking (bookings)">
        <FieldGrid
          fields={[
            ["Trace ID", b.trace_id],
            ["User ID", b.user_id],
            ["User Type ID", b.user_type_id],
            ["Status", b.status === 1 ? "1 (BOOKED)" : b.status],
            ["PNR", b.pnr],
            ["TBO Booking ID (flight_id)", b.flight_id],
            ["Airline PNR", b.airline_pnr],
            ["Airline", b.airline_code],
            ["Origin", b.origin],
            ["Destination", b.destination],
            ["Departure", fmtDateTime(b.dep_date_time)],
            ["Arrival", fmtDateTime(b.arr_date_time)],
            ["Journey Type", b.journey_type],
            ["Low Cost", b.is_low_cost ? "Yes" : "No"],
            ["Domestic", b.is_domestic ? "Yes" : "No"],
            ["Craft", b.airline_craft],
            ["Base Fare", b.base_fare],
            ["Total Fare", b.totalfare],
            ["Published Fare", b.published_fare],
            ["Convenience Fee", b.convenience_fee],
            ["NavCash Earn (cashback_points)", b.cashback_points],
            ["CGST / SGST / IGST", `${show(b.cgst)} / ${show(b.sgst)} / ${show(b.igst)}`],
            ["Refundable", b.is_refundable == null ? null : b.is_refundable ? "Yes" : "No"],
            ["Flight No", b.airline_flight_no],
            ["Fare Type", b.fare_type],
            ["Payment ID", paymentIdOverride || b.payment_id],
          ]}
        />
        <p className="mt-3 text-xs text-gray-500">
          Booking ID, ticket reference (NAV...) and created time are generated when the booking is created.
        </p>
      </Section>

      <Section title="Fare Details (fare_details)">
        <FieldGrid
          fields={[
            ["Currency", fd?.currency],
            ["Base Fare", fd?.base_fare],
            ["Tax", fd?.tax],
            ["YQ Tax", fd?.yq_tax],
            ["Published Fare", fd?.published_fare],
            ["Offered Fare", fd?.offered_fare],
            ["Commission Earned", fd?.commission_earned],
            ["TDS on Commission", fd?.tds_on_commision],
            ["Other Charges", fd?.other_charges],
            ["Service Fee", fd?.service_fee],
            ["Discount", fd?.discount],
            ["PG Charge", fd?.pg_charge],
          ]}
        />
      </Section>

      <Section title={`Passengers (${rows.passengers?.length || 0})`}>
        <SimpleTable
          rows={rows.passengers}
          columns={[
            ["Lead", (p) => (p.is_lead_pax ? "Yes" : "")],
            ["Name", (p) => [p.title, p.first_name, p.last_name].filter(Boolean).join(" ")],
            ["Pax Type", (p) => p.pax_type],
            ["DOB", (p) => (p.date_of_birth ? String(p.date_of_birth).slice(0, 10) : null)],
            ["Passport", (p) => p.passport_no],
            ["Email", (p) => p.email],
            ["Contact", (p) => p.contact_no],
            ["User ID", (p) => p.user_id],
            ["Ticket #", (p) => p.ticket_number],
          ]}
        />
      </Section>

      <Section title={`Segments (${rows.segments?.length || 0})`}>
        <SimpleTable
          rows={rows.segments}
          columns={[
            ["Trip", (s) => s.trip_indicator],
            ["Flight", (s) => [s.airline?.airline_code, s.airline?.flight_number].filter(Boolean).join(" ")],
            ["From", (s) => s.origin?.airport?.airport_code ?? s.origin?.airport_code],
            ["Departure", (s) => fmtDateTime(s.origin?.dep_time)],
            ["To", (s) => s.destination?.airport?.airport_code ?? s.destination?.airport_code],
            ["Arrival", (s) => fmtDateTime(s.destination?.arr_time)],
            ["Duration", (s) => s.duration],
            ["Baggage", (s) => s.baggage],
            ["Cabin Baggage", (s) => s.cabin_baggage],
            ["Seat", (s) => segmentAddons(rows.passengers, s, "seat", seatLabel)],
            ["Meal", (s) => segmentAddons(rows.passengers, s, "meal", mealLabel)],
            ["Status", (s) => s.flight_status],
          ]}
        />
      </Section>
    </>
  );
}

/** Checkout fee calculation result (fares() output) for one booking / leg. */
// Calculate Fee discount pickers, shared by the one-way and round-trip pages.
// Only one of promo code / gift card / NavCash applies: picking one clears the other.
const REDEEM_OPTIONS = [
  { value: "", label: "None" },
  { value: "giftcard", label: "Gift Card" },
  { value: "navcash", label: "Wallet (NavCash)" },
];

// onChange(promoCode, redeem, redeemAmount): redeemAmount "" means the maximum.
// The server caps any amount at the balance / per-booking limit.
export function DiscountControls({ result, promoCode, redeem, redeemAmount = "", disabled, onChange }) {
  const promos = result.promo_codes || [];
  const options = result.redeem_options;
  const summary = result.combined_fare_summary || result.fare_summary;
  const maxAmount =
    redeem === "giftcard" ? options?.giftcard?.available_balance : redeem === "navcash" ? options?.navcash?.redeemable : null;
  // What the server actually applied, shown until the admin types a new amount.
  const appliedAmount =
    redeem === "giftcard" ? summary?.applied_giftcard_amount : redeem === "navcash" ? summary?.wallet_points : null;
  const [draft, setDraft] = useState("");
  useEffect(() => {
    setDraft(redeemAmount !== "" ? String(redeemAmount) : appliedAmount != null ? String(appliedAmount) : "");
  }, [redeem, redeemAmount, appliedAmount]);

  const redeemHint =
    redeem === "navcash"
      ? `Max ${show(maxAmount)} (balance ${show(options?.navcash?.available_balance)}, cap ${show(options?.navcash?.cap)} per booking)`
      : redeem === "giftcard"
        ? `Max ${show(maxAmount)} (gift card balance, up to the payable total)`
        : null;

  const applyDraft = () => {
    const value = draft.trim();
    if (value !== "" && !(Number(value) > 0)) {
      toast.error("Enter an amount greater than 0");
      return;
    }
    if (value === String(redeemAmount)) return;
    // Blurring without editing the shown applied amount changes nothing.
    if (redeemAmount === "" && appliedAmount != null && value === String(appliedAmount)) return;
    onChange(promoCode, redeem, value);
  };

  // Only one discount applies: choosing a promo clears Redeem and vice versa.
  return (
    <div className="space-y-2">
    <div className="text-xs text-gray-500">Apply any one: a promo code, the gift card, or wallet (NavCash).</div>
    <div className="flex flex-wrap items-end gap-3">
      <div className="w-full sm:w-auto sm:min-w-[260px]">
        <label className="text-sm font-semibold">Promo Code</label>
        <select
          className="mt-2 w-full border rounded-xl px-3 py-2 bg-white outline-none focus:ring-2 focus:ring-black"
          value={promoCode}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value, e.target.value ? "" : redeem, e.target.value ? "" : redeemAmount)}
        >
          <option value="">{promos.length ? "No promo code" : "No eligible promo codes"}</option>
          {promos.map((p) => (
            <option key={p.promo_id} value={p.code}>
              {p.code}
              {p.preview_text ? ` · ${p.preview_text}` : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="w-full sm:w-auto sm:min-w-[200px]">
        <label className="text-sm font-semibold">Redeem</label>
        <select
          className="mt-2 w-full border rounded-xl px-3 py-2 bg-white outline-none focus:ring-2 focus:ring-black"
          value={redeem}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value ? "" : promoCode, e.target.value, "")}
        >
          {REDEEM_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {redeem && (
        <div className="w-full sm:w-auto sm:min-w-[200px]">
          <label className="text-sm font-semibold">{redeem === "giftcard" ? "Gift Card Balance" : "NavCash Redeemable"}</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 bg-gray-50 outline-none"
            type="number"
            min="0"
            step="0.01"
            placeholder={maxAmount != null ? `Max ${maxAmount}` : ""}
            value={draft}
            disabled={disabled}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={applyDraft}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyDraft();
              }
            }}
          />
          {redeemHint && <div className="mt-1 text-xs text-gray-500">{redeemHint}</div>}
        </div>
      )}
    </div>
    </div>
  );
}

export function FareSummaryView({ summary }) {
  if (!summary) return null;
  // NAVCASH promos give cashback after travel instead of a discount, so it is
  // shown under Total Payable and never added into it.
  const promoCashback = Number(summary.navcash_cashback_amount) || 0;
  const navcashEarn = Number(summary.cashback_wallet_one_percentage) || 0;
  return (
    <div className="space-y-4">
      <table className="w-full max-w-md text-sm">
        <tbody>
          {(summary.fare_info || []).map((f, i) => (
            <tr key={`${f.label}-${i}`} className="border-t first:border-t-0">
              <td className="py-2 text-gray-600">
                {f.label}
                {f.label === "Convenience Fee" && summary.convenience_fee_gst != null && (
                  <span className="ml-1 text-xs text-gray-500">
                    (GST: {Number(summary.convenience_fee_gst).toFixed(2)} included)
                  </span>
                )}
              </td>
              <td className="py-2 text-right font-semibold">{Number(f.value || 0).toFixed(2)}</td>
            </tr>
          ))}
          <tr className="border-t-2">
            <td className="py-2 font-bold">Total Payable</td>
            <td className="py-2 text-right font-bold">{summary.total}</td>
          </tr>
          {promoCashback > 0 && (
            <tr className="border-t text-green-700">
              <td className="py-2">
                Promo Cashback{summary.promo_code ? ` (${summary.promo_code})` : ""}
                <span className="ml-1 text-xs">credited after travel</span>
              </td>
              <td className="py-2 text-right font-semibold">{promoCashback.toFixed(2)}</td>
            </tr>
          )}
        </tbody>
      </table>
      <FieldGrid
        fields={[
          ["Total Amount (before fee)", summary.totalAmount],
          [
            "Convenience Fee",
            summary.convenience_fee_gst != null
              ? `${summary.convenienceFee} (GST: ${Number(summary.convenience_fee_gst).toFixed(2)} included)`
              : summary.convenienceFee,
          ],
          ...(promoCashback > 0
            ? [
                ["Promo Cashback", promoCashback.toFixed(2)],
                ["NavCash Earn (booking)", navcashEarn],
                ["Total NavCash", summary.total_navcash_cashback],
              ]
            : [["NavCash Earn", summary.total_navcash_cashback]]),
          ["Payment Mode", summary.payable_payment_mode],
        ]}
      />
      <JsonPanel title="Fare Summary" data={summary} />
    </div>
  );
}

/**
 * Confirm returns as soon as the booking is saved; push / ticket email /
 * invoice go out in the background, so poll their outcome here.
 */
export function useNotificationPolling(bookingId, status, onFinish) {
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  useEffect(() => {
    if (!bookingId || status !== "running") return;
    let cancelled = false;
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const res = await api.get(`/admin/bookings/create-from-tbo/${bookingId}/notifications`);
        const job = res.data?.data;
        if (cancelled || !job) return;
        if (job.status === "done" || job.status === "failed") {
          clearInterval(timer);
          onFinishRef.current({ status: job.status, notifications: job.notifications || {} });
          if (job.status === "done") toast.success("Notifications sent");
          else toast.error("Some notifications failed");
          return;
        }
      } catch {
        // keep polling; a single failed poll is not fatal
      }
      if (attempts >= 60) {
        clearInterval(timer);
        onFinishRef.current({ status: "timeout", notifications: null });
      }
    }, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [bookingId, status]);
}
